import { type ReactNode, createContext, useContext } from "react";

/**
 * Feature toggles configuration.
 */
export interface FeaturesConfig {
	/**
	 * Enable navigation guards (unsaved changes, session timeout).
	 * @default true
	 */
	guards?: boolean;

	/**
	 * Force showTotalCount for all lazy (non-eager) tables.
	 * When enabled, all lazy tables will request total row count from backend,
	 * overriding individual table's showTotalCount setting.
	 * @default false
	 */
	forceShowTotalCountForLazyTables?: boolean;

	/**
	 * Enable inline header filters in DataGrid tables.
	 * Requires MUI X Pro license (`muiProLicenseKey` must be set).
	 * When enabled, each column header shows an inline filter input,
	 * replacing the standalone filter panel for a more streamlined UX.
	 * @default false
	 */
	headerFilters?: boolean;
}

/**
 * Runtime configuration available to components.
 */
export interface RuntimeConfig {
	features?: FeaturesConfig;
	/**
	 * UI density level from theme configuration.
	 * @default 'compact'
	 */
	density?: "compact" | "comfortable" | "standard";
	/**
	 * MUI X Pro license key.
	 * When set, the runtime will activate MUI Pro and automatically wire in
	 * Pro variants of MUI X components (DataGridPro, DatePickerPro, etc.).
	 * Requires `@mui/x-data-grid-pro` (and/or other Pro packages) to be installed.
	 */
	muiProLicenseKey?: string;
}

/**
 * RuntimeConfig context provides access to runtime feature flags.
 */
export interface RuntimeConfigContextType {
	config: RuntimeConfig;
}

const RuntimeConfigContext = createContext<RuntimeConfigContextType | null>(null);

export interface RuntimeConfigProviderProps {
	config?: RuntimeConfig;
	children: ReactNode;
}

/**
 * Provider for runtime configuration.
 * Typically wraps the app at a high level (e.g., in JudoRuntime).
 */
export function RuntimeConfigProvider({ config = {}, children }: RuntimeConfigProviderProps) {
	return <RuntimeConfigContext.Provider value={{ config }}>{children}</RuntimeConfigContext.Provider>;
}

/**
 * Hook to access runtime configuration.
 * Throws if used outside RuntimeConfigProvider.
 */
export function useRuntimeConfig(): RuntimeConfigContextType {
	const context = useContext(RuntimeConfigContext);
	if (!context) {
		throw new Error("useRuntimeConfig must be used within RuntimeConfigProvider");
	}
	return context;
}

/**
 * Hook to access runtime configuration (optional).
 * Returns null if used outside RuntimeConfigProvider.
 */
export function useRuntimeConfigOptional(): RuntimeConfigContextType | null {
	return useContext(RuntimeConfigContext);
}
